import {
  CreateDatasetItemRequest,
  CreateDatasetRequest,
  Dataset,
  DatasetFilters,
  DatasetItem,
  DatasetItemFilters,
  PaginatedResponse,
  PaginationParams,
  UpdateDatasetRequest
} from "../../types/dataset";
import { PromiseResponse } from "../../types/shared";
import { logError } from "../../utils/error";
import { supabase } from "../supabase/client";

/**
 * Fetches all datasets for the current tenant with optional filters
 */
export const fetchDatasets = async (
  filters?: DatasetFilters
): PromiseResponse<Dataset[]> => {
  try {
    let query = supabase
      .from("v_calibration_dataset")
      .select("*")
      .order("created_at", { ascending: false });

    if (filters?.search) {
      query = query.or(`dataset_name.ilike.%${filters.search}%,dataset_description.ilike.%${filters.search}%`);
    }

    if (filters?.dateFrom) {
      query = query.gte("created_at", filters.dateFrom);
    }

    if (filters?.dateTo) {
      query = query.lte("created_at", filters.dateTo);
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: data as Dataset[], error: null };
  } catch (error) {
    logError("Error fetching datasets", error);
    return { success: false, error: "Failed to fetch datasets", data: null };
  }
};

/**
 * Fetches datasets with pagination
 */
export const fetchDatasetsPaginated = async (
  pagination: PaginationParams,
  filters?: DatasetFilters
): PromiseResponse<PaginatedResponse<Dataset>> => {
  try {
    const offset = (pagination.page - 1) * pagination.limit;

    let query = supabase
      .from("v_calibration_dataset")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + pagination.limit - 1);

    if (filters?.search) {
      query = query.or(`dataset_name.ilike.%${filters.search}%,dataset_description.ilike.%${filters.search}%`);
    }

    if (filters?.dateFrom) {
      query = query.gte("created_at", filters.dateFrom);
    }

    if (filters?.dateTo) {
      query = query.lte("created_at", filters.dateTo);
    }

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    const totalPages = Math.ceil((count || 0) / pagination.limit);

    const result: PaginatedResponse<Dataset> = {
      data: data as Dataset[],
      total: count || 0,
      page: pagination.page,
      limit: pagination.limit,
      totalPages
    };

    return { success: true, data: result, error: null };
  } catch (error) {
    logError("Error fetching paginated datasets", error);
    return { success: false, error: "Failed to fetch datasets", data: null };
  }
};

/**
 * Fetches a single dataset by ID
 */
export const fetchDataset = async (id: string): PromiseResponse<Dataset> => {
  try {
    const { data, error } = await supabase
      .from("v_calibration_dataset")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: data as Dataset, error: null };
  } catch (error) {
    logError("Error fetching dataset", error);
    return { success: false, error: "Failed to fetch dataset", data: null };
  }
};

/**
 * Creates a new dataset
 */
export const createDataset = async (
  dataset: CreateDatasetRequest
): PromiseResponse<Dataset> => {
  try {
    const { data, error } = await supabase
      .from("calibration_dataset")
      .insert({
        dataset_name: dataset.dataset_name,
        dataset_description: dataset.dataset_description,
        tenant_id: dataset.tenant_id
      })
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: data as Dataset, error: null };
  } catch (error) {
    logError("Error creating dataset", error);
    return { success: false, error: "Failed to create dataset", data: null };
  }
};

/**
 * Updates an existing dataset
 */
export const updateDataset = async (
  id: string,
  updates: UpdateDatasetRequest
): PromiseResponse<Dataset> => {
  try {
    const { data, error } = await supabase
      .from("calibration_dataset")
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: data as Dataset, error: null };
  } catch (error) {
    logError("Error updating dataset", error);
    return { success: false, error: "Failed to update dataset", data: null };
  }
};

/**
 * Deletes a dataset by ID
 */
export const deleteDataset = async (id: string): PromiseResponse<void> => {
  try {
    const { error } = await supabase
      .from("calibration_dataset")
      .delete()
      .eq("id", id);

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: undefined, error: null };
  } catch (error) {
    logError("Error deleting dataset", error);
    return { success: false, error: "Failed to delete dataset", data: null };
  }
};

/**
 * Fetches dataset items for a specific dataset with optional filters
 */
export const fetchDatasetItems = async (
  datasetId: string,
  filters?: DatasetItemFilters
): PromiseResponse<DatasetItem[]> => {
  try {
    let query = supabase
      .from("v_calibration_dataset_item")
      .select("*")
      .eq("dataset_id", datasetId)
      .order("created_at", { ascending: false });

    if (filters?.artifact_type) {
      query = query.eq("artifact_type", filters.artifact_type);
    }

    if (filters?.annotation_type) {
      query = query.eq("annotation_type", filters.annotation_type);
    }

    if (filters?.annotation_category) {
      query = query.ilike("annotation_category", `%${filters.annotation_category}%`);
    }

    if (filters?.dateFrom) {
      query = query.gte("created_at", filters.dateFrom);
    }

    if (filters?.dateTo) {
      query = query.lte("created_at", filters.dateTo);
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: data as DatasetItem[], error: null };
  } catch (error) {
    logError("Error fetching dataset items", error);
    return { success: false, error: "Failed to fetch dataset items", data: null };
  }
};

/**
 * Fetches dataset items with pagination
 */
export const fetchDatasetItemsPaginated = async (
  datasetId: string,
  pagination: PaginationParams,
  filters?: DatasetItemFilters
): PromiseResponse<PaginatedResponse<DatasetItem>> => {
  try {
    const offset = (pagination.page - 1) * pagination.limit;

    let query = supabase
      .from("v_calibration_dataset_item")
      .select("*", { count: "exact" })
      .eq("dataset_id", datasetId)
      .order("created_at", { ascending: false })
      .range(offset, offset + pagination.limit - 1);

    if (filters?.artifact_type) {
      query = query.eq("artifact_type", filters.artifact_type);
    }

    if (filters?.annotation_type) {
      query = query.eq("annotation_type", filters.annotation_type);
    }

    if (filters?.annotation_category) {
      query = query.ilike("annotation_category", `%${filters.annotation_category}%`);
    }

    if (filters?.dateFrom) {
      query = query.gte("created_at", filters.dateFrom);
    }

    if (filters?.dateTo) {
      query = query.lte("created_at", filters.dateTo);
    }

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    const totalPages = Math.ceil((count || 0) / pagination.limit);

    const result: PaginatedResponse<DatasetItem> = {
      data: data as DatasetItem[],
      total: count || 0,
      page: pagination.page,
      limit: pagination.limit,
      totalPages
    };

    return { success: true, data: result, error: null };
  } catch (error) {
    logError("Error fetching paginated dataset items", error);
    return { success: false, error: "Failed to fetch dataset items", data: null };
  }
};

/**
 * Creates a new dataset item
 */
export const createDatasetItem = async (
  item: CreateDatasetItemRequest
): PromiseResponse<DatasetItem> => {
  try {
    const { data, error } = await supabase
      .from("calibration_dataset_item")
      // NOTE(types): app-level `artifact_type`/`annotation_*` unions and `artifact_config`/`expected_value` shapes are narrower than the generated Insert type.
      .insert({
        dataset_id: item.dataset_id,
        interaction_id: item.interaction_id,
        artifact_id: item.artifact_id,
        artifact_type: item.artifact_type,
        artifact_content: item.artifact_content,
        artifact_config: item.artifact_config,
        annotation_type: item.annotation_type,
        annotation_category: item.annotation_category,
        annotation_description: item.annotation_description,
        annotation_notes: item.annotation_notes,
        expected_value: item.expected_value
      } as never)
      .select()
      .single();

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: data as DatasetItem, error: null };
  } catch (error) {
    logError("Error creating dataset item", error);
    return { success: false, error: "Failed to create dataset item", data: null };
  }
};

/**
 * Deletes a dataset item by ID
 */
export const deleteDatasetItem = async (id: string): PromiseResponse<void> => {
  try {
    const { error } = await supabase
      .from("calibration_dataset_item")
      .delete()
      .eq("id", id);

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: undefined, error: null };
  } catch (error) {
    logError("Error deleting dataset item", error);
    return { success: false, error: "Failed to delete dataset item", data: null };
  }
};

/**
 * Deletes multiple dataset items by IDs
 */
export const deleteDatasetItems = async (ids: string[]): PromiseResponse<void> => {
  try {
    const { error } = await supabase
      .from("calibration_dataset_item")
      .delete()
      .in("id", ids);

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: undefined, error: null };
  } catch (error) {
    logError("Error deleting dataset items", error);
    return { success: false, error: "Failed to delete dataset items", data: null };
  }
};

/**
 * Exports a dataset as JSON
 */
export const exportDataset = async (id: string): PromiseResponse<any> => {
  try {
    const datasetResult = await fetchDataset(id);
    if (!datasetResult.success || !datasetResult.data) {
      return { success: false, error: "Dataset not found", data: null };
    }

    const itemsResult = await fetchDatasetItems(id);
    if (!itemsResult.success) {
      return { success: false, error: itemsResult.error, data: null };
    }

    const exportData = {
      dataset: datasetResult.data,
      items: itemsResult.data,
      exportedAt: new Date().toISOString(),
      version: "1.0"
    };

    return { success: true, data: exportData, error: null };
  } catch (error) {
    logError("Error exporting dataset", error);
    return { success: false, error: "Failed to export dataset", data: null };
  }
};
